import React, { useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  Polyline,
  Tooltip,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import styles from "./MapWrap.module.scss";
import type { RouteData } from "../../types/routes";

import driverIcon from "../../assets/volunteer-pin.png";
import pinIcon from "../../assets/user-pin.png";
import pinPharmacy from "../../assets/pharmacy-pin.png";

const LdriverIcon = new L.Icon({
  iconUrl: driverIcon,
  iconSize: [48, 55],
  iconAnchor: [24, 55],
});
const LpinIcon = new L.Icon({
  iconUrl: pinIcon,
  iconSize: [48, 55],
  iconAnchor: [24, 55],
});
const LPharmacyIcon = new L.Icon({
  iconUrl: pinPharmacy,
  iconSize: [48, 55],
  iconAnchor: [24, 55],
});

// Error boundary component for map rendering
class MapErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(
      "[MapWrap] Map rendering error:",
      error.message,
      errorInfo.componentStack,
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: "20px",
            textAlign: "center",
            color: "#666",
            border: "1px solid #ddd",
            borderRadius: "4px",
            backgroundColor: "#f9f9f9",
            width: "100%",
            aspectRatio: "1",
          }}
        >
          Mapa trenutno nije dostupna
        </div>
      );
    }

    return this.props.children;
  }
}

interface MapWrapProps {
  pharmacyLocation: [number, number] | null;
  customerLocation: [number, number] | null;
  userLocation: [number, number] | null;
  routeData?: RouteData;
  routeLoading?: boolean; // NEW: Route loading state
  routeError?: string | null; // NEW: Route error state
}

const MapWrap = ({
  pharmacyLocation,
  customerLocation,
  userLocation,
  routeData,
  routeLoading = false, // NEW: Route loading state
  routeError = null, // NEW: Route error state
}: MapWrapProps) => {
  // Suppress unused variable warnings - these are for future use
  void routeLoading;
  void routeError;
  const position: [number, number] = [44.79, 20.4538];

  // console.log("[MapWrap] Pins-only mode - showing locations without routes");

  const pharmacyLocationCheck =
    pharmacyLocation && pharmacyLocation?.every((item: any) => item !== null)
      ? pharmacyLocation
      : null;
  const customerLocationCheck =
    customerLocation && customerLocation?.every((item: any) => item !== null)
      ? customerLocation
      : null;
  const userLocationCheck =
    userLocation && userLocation?.every((item: any) => item !== null)
      ? userLocation
      : null;

  return (
    <div>
      <MapErrorBoundary>
        <MapContainer
          scrollWheelZoom={false}
          attributionControl={false}
          center={position}
          zoom={13}
          style={{ width: "100%", aspectRatio: "1", zIndex: "9" }}
        >
          <TileLayer
            attribution=""
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          />

          {userLocationCheck && (
            <Marker position={userLocationCheck} icon={LdriverIcon}>
              <Tooltip>Volonter</Tooltip>
            </Marker>
          )}

          {pharmacyLocationCheck && (
            <Marker position={pharmacyLocationCheck} icon={LPharmacyIcon}>
              <Tooltip>Apoteka</Tooltip>
            </Marker>
          )}

          {customerLocationCheck && (
            <Marker position={customerLocationCheck} icon={LpinIcon}>
              <Tooltip>Kupac</Tooltip>
            </Marker>
          )}

          {/* PHARMACY TO CUSTOMER ROUTE - Main focus route */}
          {pharmacyLocationCheck &&
            customerLocationCheck &&
            routeData?.pharmacyToCustomer?.shape && (
              <Polyline
                positions={routeData.pharmacyToCustomer.shape}
                color="#FF6B35" // Orange color for pharmacy-customer route
                opacity={0.8}
                weight={4}
              />
            )}

          {/* User to customer and user to pharmacy routes - kept for future use */}
          {/*
        {userLocationCheck && customerLocationCheck && routeData?.userToCustomer?.shape && (
          <Polyline positions={routeData.userToCustomer.shape} color="#3BD4AE" opacity={0.8} weight={4}>
            <Tooltip className={styles.tooltipStyles} permanent direction="top" offset={[0, -10]} opacity={1}>
              {routeData.userToCustomer.distance}
              {routeData.userToCustomer.duration && ` • ${routeData.userToCustomer.duration}`}
            </Tooltip>
          </Polyline>
        )}
        
        {pharmacyLocationCheck && userLocationCheck && routeData?.userToPharmacy?.shape && (
          <Polyline positions={routeData.userToPharmacy.shape} color="#FF5500" opacity={0.8} weight={4}>
            <Tooltip className={styles.tooltipStyles} permanent direction="top" offset={[0, -10]} opacity={1}>
              {routeData.userToPharmacy.distance}
              {routeData.userToPharmacy.duration && ` • ${routeData.userToPharmacy.duration}`}
            </Tooltip>
          </Polyline>
        )}
        */}

          <FitBounds
            pharmacyLocation={pharmacyLocationCheck}
            customerLocation={customerLocationCheck}
            userLocation={userLocationCheck}
          />
        </MapContainer>
      </MapErrorBoundary>
    </div>
  );
};

export default MapWrap;

const FitBounds = ({
  pharmacyLocation,
  customerLocation,
  userLocation,
}: any) => {
  const map = useMap();

  let locations: any[] = [];

  if (pharmacyLocation) {
    locations.push(pharmacyLocation);
  }
  if (customerLocation) {
    locations.push(customerLocation);
  }
  if (userLocation) {
    locations.push(userLocation);
  }

  useEffect(() => {
    if (locations.length > 0) {
      const bounds = new L.LatLngBounds(locations);
      map.fitBounds(bounds, { padding: [40, 40], animate: true });
    }
  }, [map, locations]);

  return null;
};
